// Detalhes.jsx
// ------------------------------------------------------------
// PÃ¡gina responsÃ¡vel por exibir os detalhes completos de um projeto.
//
// FunÃ§Ãµes principais:
// âœ” Buscar o projeto pelo ID da rota
// âœ” Renderizar mÃ­dia dinÃ¢mica (imagem, vÃ­deo ou YouTube)
// âœ” Exibir informaÃ§Ãµes completas (tÃ­tulo, descriÃ§Ã£o, tags)
// âœ” Exibir links de disponibilidade (com Ã­cones)
// âœ” Mostrar projetos relacionados
// âœ” Controlar o comportamento do carrossel horizontal
// âœ” Garantir melhor experiÃªncia de navegaÃ§Ã£o (scroll automÃ¡tico)
//
// PadrÃµes adotados:
// âœ” Hooks sempre no topo
// âœ” SeparaÃ§Ã£o clara entre dados, lÃ³gica e renderizaÃ§Ã£o
// âœ” Interface controlada por dados (JSON)
// âœ” CÃ³digo defensivo para evitar erros com dados ausentes
// ------------------------------------------------------------

import { React, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Hooks do React Router
import Card from "../components/Card/Card"; // Card reutilizÃ¡vel
import iconesDisponibilidade from "../img/configuracaoDisponibilidade"; // Mapeamento de Ã­cones por tipo
import { api } from "../services/api";
import { normalizarLinkExterno, normalizarProjetoBackend } from "../utils/projetos";
import { useProjetosBanco } from "../hooks/useProjetosComBanco";

function Detalhes() {

  // ------------------------------------------------------------
  // ðŸ”¹ HOOKS (sempre no topo do componente)
  // ------------------------------------------------------------

  // ReferÃªncia para o container do carrossel (scroll horizontal)
  const containerRef = useRef(null);

  // Estados que controlam a visibilidade das setas do carrossel
  const [podeIrEsquerda, setPodeIrEsquerda] = useState(false);
  const [podeIrDireita, setPodeIrDireita] = useState(true);

  // Captura o ID da URL (/projeto/:id)
  const { id } = useParams();

  // Hook de navegaÃ§Ã£o
  const navigate = useNavigate();
  const projetosBanco = useProjetosBanco();

  // ------------------------------------------------------------
  // ðŸ”¹ EFEITO: RESET DE SCROLL

  // ðŸ”¹ EFEITO: RESET DE SCROLL
  // ------------------------------------------------------------
  // Sempre que o ID muda (troca de projeto),
  // a pÃ¡gina volta suavemente para o topo.
  //
  // Isso melhora a UX, pois o React Router nÃ£o recarrega a pÃ¡gina,
  // entÃ£o o scroll anterior seria mantido.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, [id]);

  // ------------------------------------------------------------
  // ðŸ”¹ EFEITO: CONTROLE DO SCROLL DO CARROSSEL
  // ------------------------------------------------------------
  // Adiciona listeners para:
  // âœ” scroll do container
  // âœ” resize da janela
  //
  // Isso permite atualizar dinamicamente
  // a visibilidade das setas (esquerda/direita)
  useEffect(function () {
    const container = containerRef.current;

    if (!container) return;

    function atualizar() {
      verificarScroll();
    }

    // Verifica estado inicial
    verificarScroll();

    container.addEventListener("scroll", atualizar);
    window.addEventListener("resize", atualizar);

    // Cleanup para evitar vazamento de memÃ³ria
    return function () {
      container.removeEventListener("scroll", atualizar);
      window.removeEventListener("resize", atualizar);
    };
  }, [id]);

  // ------------------------------------------------------------
  // ðŸ”¹ DADOS DO PROJETO
  // ------------------------------------------------------------

  const routeId = id;

  const [projeto, setProjeto] = useState(null);
  const [carregandoProjeto, setCarregandoProjeto] = useState(true);
  const [erroProjeto, setErroProjeto] = useState("");

  function mapCardToItem(card) {
    return normalizarProjetoBackend(card);
  }

  useEffect(() => {
    let ativo = true;

    async function carregarProjeto() {
      setCarregandoProjeto(true);
      setErroProjeto("");
      try {
        const ehBackend = typeof routeId === "string" && routeId.startsWith("backend-");
        const numericId = ehBackend ? Number(routeId.replace("backend-", "")) : Number(routeId);
        const response = await api.get(`/cards/${numericId}`);
        if (!ativo) return;
        setProjeto(mapCardToItem(response.data));
      } catch (error) {
        if (!ativo) return;
        setProjeto(undefined);
        setErroProjeto("Projeto nÃ£o encontrado.");
      } finally {
        if (!ativo) return;
        setCarregandoProjeto(false);
      }
    }

    carregarProjeto();

    return () => {
      ativo = false;
    };
  }, [routeId]);

  if (carregandoProjeto) {
    return (
      <div style={{ padding: 24 }}>
        <p>Carregando projeto...</p>
      </div>
    );
  }

  if (!projeto || erroProjeto) {
    return (
      <div style={{ padding: 24 }}>
        <p>{erroProjeto || "Projeto nÃ£o encontrado."}</p>
        <button onClick={() => navigate(-1)}>Voltar</button>
      </div>
    );
  }

  // ------------------------------------------------------------
  // ðŸ”¹ LÃ“GICA DERIVADA DO PROJETO
  // ------------------------------------------------------------

  const { apresentacao } = projeto;

  // Filtra projetos relacionados com base na tag principal
  const todosProjetos = [...projetosBanco];
  const relacionado = todosProjetos
  .filter(function (p) {

    if (p.id === projeto.id) return false;
    if (!p.tags || !projeto.tags) return false;

    // ðŸ”¥ Verifica se existe PELO MENOS UMA tag em comum
    return p.tags.some(function (tag) {
      return projeto.tags.some(function (ptag) {
        return String(ptag).toLowerCase() === String(tag).toLowerCase();
      });
    });

  })
  .slice(0, 4); // limita a 4 itens

  // ------------------------------------------------------------
  // ðŸ”¹ CONTROLE DE SCROLL DO CARROSSEL
  // ------------------------------------------------------------

  function verificarScroll() {
    const container = containerRef.current;

    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Atualiza estado das setas
    setPodeIrEsquerda(scrollLeft > 0);
    setPodeIrDireita(scrollLeft + clientWidth < scrollWidth - 1);
  }

  // Move o carrossel para a esquerda
  function scrollLeft() {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  }

  // Move o carrossel para a direita
  function scrollRight() {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  }

  // ------------------------------------------------------------
  // ðŸ”¹ RENDERIZAÃ‡ÃƒO
  // ------------------------------------------------------------

  return (
    <main className="detalhes">

      {/* ðŸ”¹ Ãrea de topo (background/estÃ©tica) */}
      <section className="detalhes-topo"></section>

      {/* ðŸ”¹ ConteÃºdo principal */}
      <section className="detalhes-conteudo">

        <div className="detalhes-container">

          {/* -------------------------------------------------- */}
          {/* ðŸ”¹ MÃDIA DINÃ‚MICA DO PROJETO */}
          {/* -------------------------------------------------- */}
          <div className="detalhe-media">

            {/* Imagem */}
            {apresentacao.tipo === "imagem" && (
              <img
                src={apresentacao.arquivo}
                alt={projeto.titulo}
              />
            )}

            {/* VÃ­deo local */}
            {apresentacao.tipo === "video" && (
              <video controls style={{ width: "100%", maxHeight: 520 }}>
                <source src={apresentacao.arquivo} type="video/mp4" />
                Seu navegador nÃ£o suporta o elemento de vÃ­deo.
              </video>
            )}

            {/* YouTube */}
            {apresentacao.tipo === "youtube" && (
              <iframe
                title={projeto.titulo}
                src={apresentacao.url}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: 520 }}
              />
            )}

          </div>

          {/* -------------------------------------------------- */}
          {/* ðŸ”¹ INFORMAÃ‡Ã•ES DO PROJETO */}
          {/* -------------------------------------------------- */}
          <article className="detalhe-card">

            {/* CabeÃ§alho */}
            <div className="detalhe-cabecalho">

              <h1>{projeto.titulo}</h1>

              {/* Tags */}
              <div className="detalhe-tags">
                {projeto.tags.map((t) => (
                  <span key={t} className="card-tag">
                    {t}
                  </span>
                ))}
              </div>

            </div>

            {/* DescriÃ§Ã£o */}
            <div className="detalhe-info">
              <p>{projeto.info || projeto.descricao}</p>
            </div>

            {/* -------------------------------------------------- */}
            {/* ðŸ”¹ LINKS DE DISPONIBILIDADE */}
            {/* -------------------------------------------------- */}
            <div className="detalhe-links">

              {/* RenderizaÃ§Ã£o dinÃ¢mica baseada no JSON */}
              {projeto.disponibilidade && projeto.disponibilidade.map(function (item, index) {

                const config = iconesDisponibilidade[item.tipo];

                // Se nÃ£o houver configuraÃ§Ã£o para o tipo, ignora
                if (!config) {
                  return null;
                }

                return (
                  <a
                    key={index}
                    href={normalizarLinkExterno(item.url || item.link)}
                    className="detalhe-botao"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={config.icone} alt={config.label} />
                    {config.label}
                  </a>
                );

              })}

            </div>

          </article>

        </div>

        {/* -------------------------------------------------- */}
        {/* ðŸ”¹ PROJETOS RELACIONADOS */}
        {/* -------------------------------------------------- */}
        {relacionado.length > 0 && (
          <section className="relacionados">

            <div className="relacionados-container">

              <h2 className="relacionados-titulo">
                Projetos relacionados
              </h2>

              <div className="carousel-wrapper">

                {/* BotÃ£o esquerda */}
                {podeIrEsquerda && (
                  <button className="carousel-btn left" onClick={scrollLeft}>
                    ❮
                  </button>
                )}

                {/* Lista de cards */}
                <div ref={containerRef} className="cards-container">
                  {relacionado.map(function (p) {
                    return (
                      <Card
                        key={p.id}
                        projeto={p}
                        onClick={function (id) {
                          navigate(`/projeto/${id}`);
                        }}
                      />
                    );
                  })}
                </div>

                {/* BotÃ£o direita */}
                {podeIrDireita && (
                  <button className="carousel-btn right" onClick={scrollRight}>
                    ❯
                  </button>
                )}

              </div>

            </div>

          </section>
        )}

      </section>

    </main>
  );
}

export default Detalhes;


